import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Else, If, Then, When } from "react-if";

// import HBCategorySelect from "../../components/HBCategorySelect";
// import FilterSelect from "@/components/FilterSelect";
import PinList from "../../components/PinList";

import { AD_POSITION } from "@/constants/ads";
import useAppStore from "@/context/store";
import { getExpsValue } from "@/tracker/abTest";
import pvTracker from "@/tracker/report/pvTracker";
import getPageData from "@/tracker/shared/getPageData";
import { setSearch } from "@/tracker/shared/global";
import filterUrlParams from "@/utils/filterUrlParams";
import { stewardInstance } from "@/utils/steward";
import { useMount } from "ahooks";
import {
  getAds,
  getDeliveryDetail,
  getSearchPinsByQingzhou,
} from "../../services";
import { RelationWords } from "../Search/RelationWords";
import RecallSearch from "../SearchPinList/recallSearch";
import styles from "./index.module.less";

// import { SORT_LIST, SORT_ITEM_MAP } from "@/constants/sort";
// import { MATERIAL_FILE_TYPE_LIST, MATERIAL_FILE_TYPE_ITEM_MAP } from "@/constants/materialFileType";
// import { COLOR_LIST, COLOR_ITEM_MAP } from "@/constants/color";

export default function SearchMaterialList({
  query,
  empty,
  searchResult,
  setRecommend,
}) {
  const pageRef = useRef(searchResult ? 2 : 1);
  const randomNumRef = useRef(null);
  const [hasMore, setHasMore] = useState(true);
  const [dataSource, setDataSource] = useState(searchResult || []);
  const [ads, setAds] = useState([]);
  const [questionaire, setQuestionaire] = useState(null);
  const [keywordMap, setKeywordMap] = useState({});
  const [isRecallSearch, setIsRecalSearch] = useState(false);
  const [isAllMatch, setIsAllMatch] = useState(true);
  const [splitWords, setSplitWords] = useState([]);
  const [busy, setBusy] = useState(false);
  const router = useRouter();

  const { q, sort, type, filterIds } = query;
  const initRef = useRef(!searchResult);
  const { user } = useAppStore(({ user }) => ({
    user,
  }));
  const filterIdMap = useMemo(() => {
    let map = null;
    try {
      if (filterIds) {
        map = filterUrlParams.decode(filterIds);
      }
    } catch (e) {
      console.error("filter_ids对象转换失败");
    }
    return map;
  }, [filterIds]);

  const category = useMemo(() => {
    return Object.values(filterIdMap || {}).reduce((result, value) => {
      if (result) {
        return result;
      } else {
        const match = value.match(/material_category:(.*)/);
        return match && match[1];
      }
    }, "");
  }, [filterIdMap]);

  const queryAction = useMemo(() => {
    // 服务端渲染时，不做初始化的一些操作
    if (initRef.current) {
      pageRef.current = 1;
      setHasMore(true);
      setDataSource([]);
    } else {
      initRef.current = true;
    }

    return async () => {
      if (!q) {
        return;
      }
      if (pageRef.current === 1) {
        setIsRecalSearch(false);
      }
      const filter_ids = filterIdMap
        ? Object.values(filterIdMap).reduce((result, value) => {
            result.push(value);
            return result;
          }, [])
        : null;

      // 设置如果超过30秒，显示服务器繁忙，请稍等片刻...
      const timer = setTimeout(() => {
        setBusy(true);
      }, 10000);

      const searchAb = await getExpsValue("search_ab", "old");

      const result = await getSearchPinsByQingzhou({
        text: q,
        ...(sort ? { sort } : null),
        ...(filter_ids ? { filter_ids } : null),
        material: true,
        limit: 40,
        page: pageRef.current,
        position: "search_material",
        // 控制联想词数据
        ...(searchAb === "new" ? { relations: 20 } : null),
      });
      // 如果结果返回有问题直接设置繁忙，如果没问题就清楚定时器
      if (result?.err) {
        setBusy(true);
      } else {
        clearTimeout(timer);
        setBusy(false);
      }

      // 搜索推荐主题， 仅在第一页请求更新数据
      if (pageRef.current === 1) {
        if (result.relations) {
          setKeywordMap(result.relations);
        }

        if (result.rec_topic_material?.recommendType !== "material") {
          setRecommend(result.rec_topic_material);
        } else {
          setRecommend([]);
        }
      }

      const pins = result?.pins || [];
      /**
       * 展示部分匹配的情况
       * 1.素材数量<=30分词数>1,且用户登陆了
       * 2.素材数量<=30分词数>1,用户没登陆，且素材数量为0
       */
      if (
        result.total <= 30 &&
        result.split_words?.length > 1 &&
        (user || (!user && !pins.length))
      ) {
        setIsRecalSearch(true);
        // setHasMore(false);
        setSplitWords(result.split_words);
      }
      if (pins.length <= 0) {
        setHasMore(false);
      }
      setDataSource((dataSource) => dataSource.concat(pins));
    };
  }, [q, filterIdMap, sort, user, setRecommend]);

  const queryRecommend = useMemo(() => {
    return async () => {
      const result = await getAds({
        positions: [
          AD_POSITION.SEARCH_WATERFALL_FIXED_1,
          AD_POSITION.SEARCH_WATERFALL_FIXED_2,
          AD_POSITION.SEARCH_WATERFALL_RANDOM_1,
        ],
        variables: {
          user_tag: ["material"],
          category: [category || "none"],
          search_keyword: [q],
        },
      });

      setAds(
        result?.reduce((result, item) => {
          if (item) {
            result.push({
              ...item,
              pin_id: `recommend-${item.material.id}`,
              type: "recommend",
            });
          } else {
            result.push(null);
          }
          return result;
        }, []) || []
      );
    };
  }, [category, q]);

  const queryQuestionaire = useMemo(() => {
    return async () => {
      const result = await getDeliveryDetail("web-search-material-feedback");
      setQuestionaire(
        result?.pit_modules?.[0]?.material
          ? {
              type: "questionaire",
              pin_id: `questionaire-${result?.pit_modules?.[0]?.module_id}`,
              id: result?.pit_modules?.[0]?.module_id,
              name: result?.pit_modules?.[0]?.module_name,
              search_word: q,
              ...result?.pit_modules?.[0]?.material,
            }
          : null
      );
    };
  }, [q]);

  useEffect(() => {
    queryAction();
  }, [queryAction]);
  useEffect(() => {
    queryRecommend();
  }, [queryRecommend]);
  // useEffect(() => {
  //   queryKeyword();
  // }, [queryKeyword]);
  useEffect(() => {
    queryQuestionaire();
  }, [queryQuestionaire]);

  const onLoadMore = useCallback(() => {
    pageRef.current += 1;
    queryAction();
    pvTracker(getPageData());
  }, [queryAction]);

  const onChangeKeyword = (item) => {
    let searchKeyword = q;
    if (item.isReplace) {
      searchKeyword = item.keyword;
    } else {
      const keyword = Object.keys(keywordMap).reduce((result, key) => {
        if (result) {
          return result;
        }
        const item = keywordMap[key].find((item) => q.endsWith(item.keyword));
        return item?.keyword || "";
      }, "");

      if (keyword) {
        searchKeyword = searchKeyword.replace(keyword, item.keyword);
      } else {
        searchKeyword = `${q} ${item.keyword}`;
      }
    }
    setSearch({
      search_type: "筛选器-点击",
    });
    router.push({
      page: "/search",
      query: {
        q: searchKeyword,
        ...(sort ? { sort } : null),
        ...(type ? { type } : null),
        ...(filterIds ? { filter_ids: filterIds } : null),
      },
    });
  };

  useMount(() => {
    stewardInstance.getPositionDelivery({
      "/search": ["material_search_nps"],
    });
  });

  const list = useMemo(() => {
    if (dataSource.length <= 0 || ads.length <= 0) {
      return dataSource;
    }

    if (randomNumRef.current === null) {
      randomNumRef.current = parseInt(Math.random() * 10) + 11;
    }

    const num = randomNumRef.current;

    const list = [
      ...ads.slice(0, 1).filter((i) => i),
      ...dataSource.slice(0, num),
    ];

    const newList = [
      ...list.slice(0, 1),
      ...ads.slice(1, 2).filter((i) => i),
      ...list.slice(1),
      ...ads.slice(2, 3).filter((i) => i),
      ...dataSource.slice(num),
    ];

    if (newList.length > 60 && questionaire) {
      newList.splice(60, 0, questionaire);
    }

    return newList;
  }, [dataSource, ads, questionaire]);

  return (
    <div className={styles.pin} data-search-word={q}>
      <RelationWords relationList={keywordMap} />
      <If condition={dataSource.length > 0 || hasMore}>
        <Then>
          <PinList
            dataSource={list}
            hasMore={hasMore}
            isSearch={true}
            searchWord={router.query?.q}
            onLoadMore={onLoadMore}
            loadingContent={
              busy ? (
                <div className={styles.busy}>服务器繁忙，请稍等片刻...</div>
              ) : null
            }
          />
        </Then>
        <Else>{!isRecallSearch && empty}</Else>
      </If>
      <When condition={isRecallSearch && !hasMore}>
        <RecallSearch
          splitWords={splitWords}
          query={query}
          isAllMatch={isAllMatch}
          haveData={!!dataSource.length}
          type={type}
          setIsAllMatch={setIsAllMatch}
          dataSource={dataSource}
          isMaterial={true}
        ></RecallSearch>
      </When>
    </div>
  );
}
